import {
  Button,
  TextInputField,
  useModalState,
  Modal,
  IconPlus,
  Flex
} from '@sequencehq/core-components'
import { useState } from 'react'
import { useForm, type FormFields } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import AvalaraTaxCodeInput from '../common/AvalaraTaxCodeInput'

type TaxCategoryFields = FormFields<{
  name: string
  avalaraTaxCode: string
}>
type FormValues = {
  name: TaxCategoryFields['name']['value']
  avalaraTaxCode: TaxCategoryFields['avalaraTaxCode']['value']
}

interface Props {
  onCreate: (values: {
    taxCategoryName: string
    avalaraTaxCode: string
  }) => Promise<void>
}

const AddTaxCategoryForAvalaraModalUI = ({
  onCreate,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      name: '',
      avalaraTaxCode: ''
    },
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'avalaraTaxCode',
        validation: [required]
      }
    ]
  })

  const [avalaraTaxCodeInfo, setAvalaraTaxCodeInfo] = useState<
    string | undefined
  >()

  const canSubmit = queries.isValid && avalaraTaxCodeInfo

  const handleSubmit = (formValues: FormValues) => {
    if (formValues.name && formValues.avalaraTaxCode) {
      void onCreate({
        taxCategoryName: formValues.name,
        avalaraTaxCode: formValues.avalaraTaxCode
      })
    }
  }

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Add tax category
      </Modal.Header>

      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={4} mb="-17px">
          <TextInputField
            label="Name"
            data-testid="avalaraTaxCategoryModal.name"
            value={fields.name.value}
            validationErrors={fields.name.validationErrors}
            onChange={e => fields.name.onChange(e.target.value)}
            placeholder="Software"
          />
          <AvalaraTaxCodeInput
            value={fields.avalaraTaxCode.value}
            onChange={value => {
              setAvalaraTaxCodeInfo(undefined)
              fields.avalaraTaxCode.onChange(value)
            }}
            onSuccess={setAvalaraTaxCodeInfo}
          />
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            handleSubmit(queries.formData)
          }}
        >
          Create tax category
        </Button>
      </Modal.Footer>
    </>
  )
}

const AddTaxCategoryForAvalaraModal = (props: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        variant="ghost"
        leadingIcon={<IconPlus color="inherit" />}
        onClick={openModal}
        data-testid="avalaraTaxCategoryModal.trigger"
      >
        New category
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="avalaraTaxCategoryModal"
      >
        <AddTaxCategoryForAvalaraModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default AddTaxCategoryForAvalaraModal
